import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  Tooltip,
  Badge,
  Zoom,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  IconButton,
  Fade,
  CircularProgress,
} from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";
import DevicesIcon from "@mui/icons-material/Devices";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useDispatch, useSelector } from "react-redux";
import Alarm from "./Alarm";
import { fetchCamerasWithAlarm, selectCamerasWithAlarm } from "./alarmSlice";

const AlarmTestButton = ({
  cameraId,
  cameraName,
  userId,
  hasFireOrSmoke = false,
  species = [],
}) => {
  const [open, setOpen] = useState(false);
  const [alarmDevices, setAlarmDevices] = useState([]);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [triggeringSpecies, setTriggeringSpecies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [isAutoTriggered, setIsAutoTriggered] = useState(false);

  const dispatch = useDispatch();
  const camerasWithAlarm = useSelector(selectCamerasWithAlarm);

  // Log for debugging
  console.log("AlarmTestButton - Props:", {
    cameraId,
    cameraName,
    hasFireOrSmoke,
    species,
    camerasWithAlarm: camerasWithAlarm?.length,
  });

  // Find all alarm devices for this camera
  useEffect(() => {
    if (!cameraId) {
      console.log("No cameraId provided");
      return;
    }

    console.log(
      "Looking for camera",
      cameraId,
      "in camerasWithAlarm:",
      camerasWithAlarm,
    );

    if (camerasWithAlarm && camerasWithAlarm.length > 0) {
      const cameraData = camerasWithAlarm.find((c) => c.id === cameraId);
      console.log("Found camera data:", cameraData);

      if (
        cameraData?.alarm_assignments &&
        cameraData.alarm_assignments.length > 0
      ) {
        console.log("Alarm assignments found:", cameraData.alarm_assignments);
        setAlarmDevices(cameraData.alarm_assignments);
        setSelectedAlarm(cameraData.alarm_assignments[0]);
        setFetchError(false);
      } else {
        console.log("No alarm assignments for this camera");
        setAlarmDevices([]);
        setSelectedAlarm(null);
      }
    }
  }, [camerasWithAlarm, cameraId]);

  // Fetch cameras with alarm on mount
  useEffect(() => {
    console.log("Fetching cameras with alarm...");
    setLoading(true);
    setFetchError(false);
    dispatch(fetchCamerasWithAlarm())
      .then((result) => {
        console.log("Fetch result:", result);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching cameras:", error);
        setFetchError(true);
        setLoading(false);
      });

    const interval = setInterval(() => {
      dispatch(fetchCamerasWithAlarm()).catch((err) =>
        console.error("Interval fetch error:", err),
      );
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Track triggering species
  useEffect(() => {
    if (species && species.length > 0) {
      const fireSmokeSpecies = species.filter(
        (s) =>
          s.id === "fire" ||
          s.id === "smoke" ||
          s.name?.toLowerCase() === "fire" ||
          s.name?.toLowerCase() === "smoke",
      );
      console.log("Triggering species:", fireSmokeSpecies);
      setTriggeringSpecies(fireSmokeSpecies);
    }
  }, [species]);

  // Check if this event should be auto-triggered (you'll need to get this from props or API)
  // For now, we'll assume all events with fire/smoke are manual tests unless specified otherwise
  useEffect(() => {
    // This would be set by your backend when it detects an auto-trigger event
    // For now, we'll keep it false for all manual tests
    setIsAutoTriggered(false);
  }, []);

  const handleOpen = () => {
    console.log("Opening alarm dialog for camera:", cameraId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAlarmChange = (event) => {
    const selected = alarmDevices.find(
      (d) => d.alarm_device_id === event.target.value,
    );
    setSelectedAlarm(selected);
  };

  // Don't render if no fire/smoke
  if (!hasFireOrSmoke) {
    console.log("Not rendering - no fire/smoke");
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <Tooltip title="Loading alarm devices...">
        <Button
          variant="contained"
          size="small"
          disabled
          sx={{
            background: "#666",
            color: "white",
            ml: 1,
            fontWeight: "bold",
            fontSize: "0.75rem",
            minWidth: "80px",
            height: "32px",
            borderRadius: "16px",
          }}
        >
          <CircularProgress size={16} sx={{ color: "white", mr: 0.5 }} />
          LOADING
        </Button>
      </Tooltip>
    );
  }

  // Show error state
  if (fetchError) {
    return (
      <Tooltip title="Error loading alarms. Click to retry.">
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            setFetchError(false);
            setLoading(true);
            dispatch(fetchCamerasWithAlarm()).finally(() => setLoading(false));
          }}
          sx={{
            background: "#f44336",
            color: "white",
            ml: 1,
            fontWeight: "bold",
            fontSize: "0.75rem",
            minWidth: "80px",
            height: "32px",
            borderRadius: "16px",
          }}
          startIcon={<ErrorOutlineIcon />}
        >
          RETRY
        </Button>
      </Tooltip>
    );
  }

  // Show message if no alarms assigned
  if (alarmDevices.length === 0) {
    console.log("No alarm devices assigned to camera:", cameraId);
    return (
      <Tooltip title="No alarm devices connected to this camera">
        <Button
          variant="contained"
          size="small"
          disabled
          sx={{
            background: "#666",
            color: "#aaa",
            ml: 1,
            fontWeight: "bold",
            fontSize: "0.7rem",
            minWidth: "80px",
            height: "32px",
            borderRadius: "16px",
            opacity: 0.7,
          }}
        >
          NO ALARM
        </Button>
      </Tooltip>
    );
  }

  console.log("Rendering TEST button with", alarmDevices.length, "alarms");
  return (
    <>
      <Zoom in={true} style={{ transitionDelay: "100ms" }}>
        <Tooltip
          title={
            <Box sx={{ p: 1, maxWidth: 250 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", mb: 1, color: "#fff" }}
              >
                <DevicesIcon
                  sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
                />
                {alarmDevices.length} Alarm Device(s) Connected
              </Typography>
              {alarmDevices.map((device, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Chip
                    size="small"
                    label={device.alarm_name}
                    color={device.auto_trigger ? "error" : "default"}
                    variant={device.auto_trigger ? "filled" : "outlined"}
                    sx={{
                      height: 22,
                      "& .MuiChip-label": { fontSize: "11px", px: 1 },
                      bgcolor: device.auto_trigger ? "#ff4444" : "transparent",
                      borderColor: "#666",
                    }}
                  />
                  <Typography variant="caption" sx={{ color: "#aaa" }}>
                    {device.trigger_duration}s
                  </Typography>
                </Box>
              ))}
              {triggeringSpecies.length > 0 && (
                <>
                  <Divider sx={{ my: 1, bgcolor: "#333" }} />
                  <Typography
                    variant="caption"
                    sx={{ color: "#ffaa00", display: "block" }}
                  >
                    Detected: {triggeringSpecies.map((s) => s.name).join(", ")}
                  </Typography>
                </>
              )}
              {isAutoTriggered && (
                <Fade in={true}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#ff4444",
                      display: "block",
                      mt: 1,
                      fontWeight: "bold",
                    }}
                  >
                    ⚡ Auto-triggered by fire/smoke detection
                  </Typography>
                </Fade>
              )}
            </Box>
          }
          arrow
          placement="top"
        >
          <Button
            variant="contained"
            size="small"
            onClick={handleOpen}
            startIcon={<NotificationsActiveIcon />}
            sx={{
              background: "linear-gradient(45deg, #ff8c00 30%, #ffaa00 90%)",
              color: "white",
              ml: 1,
              fontWeight: "bold",
              fontSize: "0.75rem",
              "&:hover": {
                background: "#ffaa00",
              },
              minWidth: alarmDevices.length > 1 ? "90px" : "80px",
              height: "32px",
              borderRadius: "16px",
              boxShadow: "0 2px 8px rgba(255,140,0,0.3)",
            }}
          >
            TEST
            {alarmDevices.length > 1 && ` (${alarmDevices.length})`}
          </Button>
        </Tooltip>
      </Zoom>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            bgcolor: "#1e1e1e",
            color: "white",
            borderRadius: "20px",
            overflow: "hidden",
            backgroundImage:
              "linear-gradient(145deg, #1e1e1e 0%, #2a2a2a 100%)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #333",
            background: "linear-gradient(90deg, #1e1e1e 0%, #2a2a2a 100%)",
            py: 2,
            position: "relative",
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#888",
              "&:hover": { color: "#ff4444" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            pr={4}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <WarningIcon sx={{ color: "#ff8c00", fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Manual Alarm Test
              </Typography>
            </Box>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {triggeringSpecies.map((s, i) => (
                <Chip
                  key={i}
                  label={s.name}
                  size="small"
                  sx={{
                    bgcolor:
                      s.name?.toLowerCase() === "fire" ? "#ff4444" : "#ff9800",
                    color: "white",
                    height: 24,
                    fontWeight: "bold",
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ color: "#aaa" }}>
              {cameraName}
            </Typography>
            <Chip
              size="small"
              label={`${alarmDevices.length} device(s)`}
              icon={<DevicesIcon sx={{ fontSize: 14 }} />}
              sx={{ bgcolor: "#333", color: "white", height: 24 }}
            />
          </Box>

          {isAutoTriggered && (
            <Fade in={true}>
              <Alert
                severity="warning"
                sx={{
                  mt: 2,
                  bgcolor: "#ff4444",
                  color: "white",
                  borderRadius: "8px",
                  "& .MuiAlert-icon": { color: "white" },
                }}
              >
                This alarm was auto-triggered by fire/smoke detection
              </Alert>
            </Fade>
          )}
        </DialogTitle>

        <DialogContent sx={{ py: 3, bgcolor: "#1e1e1e" }}>
          {alarmDevices.length === 0 ? (
            <Typography sx={{ color: "#888", textAlign: "center", py: 4 }}>
              No alarm devices connected to this camera
            </Typography>
          ) : (
            <>
              {alarmDevices.length > 1 && (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel sx={{ color: "#888" }}>
                    Select Alarm Device
                  </InputLabel>
                  <Select
                    value={selectedAlarm?.alarm_device_id || ""}
                    onChange={handleAlarmChange}
                    sx={{
                      color: "white",
                      bgcolor: "#333",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#444",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ff8c00",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ff8c00",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#888",
                      },
                    }}
                  >
                    {alarmDevices.map((device) => (
                      <MenuItem
                        key={device.alarm_id}
                        value={device.alarm_device_id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          bgcolor: "#1e1e1e",
                          color: "white",
                          "&:hover": {
                            bgcolor: "#333",
                          },
                        }}
                      >
                        <span>{device.alarm_name}</span>
                        <Chip
                          label={device.auto_trigger ? "Auto" : "Manual"}
                          size="small"
                          color={device.auto_trigger ? "error" : "default"}
                          sx={{
                            height: 20,
                            bgcolor: device.auto_trigger ? "#ff4444" : "#555",
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {selectedAlarm ? (
                <Fade in={true}>
                  <Box>
                    <Alarm
                      key={selectedAlarm.alarm_id}
                      deviceId={selectedAlarm.alarm_device_id}
                      deviceName={selectedAlarm.alarm_name}
                      cameraId={cameraId}
                      cameraName={cameraName}
                      userId={userId}
                      autoTrigger={false} // Always false for manual tests
                      showControls={true}
                      triggerDuration={selectedAlarm.trigger_duration || 180}
                    />
                  </Box>
                </Fade>
              ) : (
                <Typography sx={{ color: "#888", textAlign: "center", py: 4 }}>
                  No alarm device selected
                </Typography>
              )}

              {alarmDevices.length > 1 && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ bgcolor: "#333", mb: 2 }} />
                  <Typography
                    variant="caption"
                    sx={{ color: "#888", display: "block", mb: 1 }}
                  >
                    All Connected Alarms:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {alarmDevices.map((device) => (
                      <Chip
                        key={device.alarm_id}
                        label={`${device.alarm_name} (${device.trigger_duration}s)`}
                        size="small"
                        color={device.auto_trigger ? "error" : "default"}
                        variant={
                          selectedAlarm?.alarm_id === device.alarm_id
                            ? "filled"
                            : "outlined"
                        }
                        onClick={() => setSelectedAlarm(device)}
                        sx={{
                          cursor: "pointer",
                          bgcolor:
                            selectedAlarm?.alarm_id === device.alarm_id
                              ? device.auto_trigger
                                ? "#ff8c00"
                                : "#555"
                              : "transparent",
                          borderColor: device.auto_trigger ? "#ff8c00" : "#666",
                          color:
                            selectedAlarm?.alarm_id === device.alarm_id
                              ? "white"
                              : "#ddd",
                          "&:hover": {
                            bgcolor: device.auto_trigger ? "#ffaa00" : "#444",
                            opacity: 0.9,
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid #333",
            p: 2,
            bgcolor: "#1a1a1a",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              bgcolor: "#ff8c00",
              color: "white",
              borderRadius: "20px",
              px: 4,
              "&:hover": {
                bgcolor: "#ffaa00",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AlarmTestButton;
